import { nextPollDelay } from "@/lib/retry/next-poll-delay";
import type { FollowSessionDependencies } from "../follow-session-dependencies";
import { followOutageMessage } from "./follow-outage-message";
import { followRetryDelay } from "./follow-retry-delay";
import type { FollowRetryState } from "./follow-retry-state";
import { remainingOutageSeconds } from "./remaining-outage-seconds";
import { retryableFollowError } from "./retryable-follow-error";
import { shouldReportFollowOutage } from "./should-report-follow-outage";

/** Wait out one failure, or rethrow the ones a follow cannot sit through. */
export async function waitForFollowRetry(params: {
  error: unknown;
  retry: FollowRetryState;
  maxOutageMs: number | null;
  dependencies: FollowSessionDependencies;
}): Promise<void> {
  const { error, retry, dependencies } = params;
  if (!retryableFollowError(error)) {
    throw error;
  }
  retry.failures += 1;
  const now = dependencies.now();
  retry.outageStartedAt ??= now;
  // Unbudgeted, a follow waits any outage out; budgeted, the sleep stops at the
  // deadline and the last failure reaches the caller as its exit code.
  const delay = nextPollDelay(
    followRetryDelay(error, retry.failures, dependencies.random),
    remainingOutageSeconds(retry.outageStartedAt, now, params.maxOutageMs),
  );
  if (delay === null) {
    throw error;
  }
  if (shouldReportFollowOutage(retry.lastWarnedAt, now)) {
    dependencies.warn(followOutageMessage(retry.warned));
    retry.warned = true;
    retry.lastWarnedAt = now;
  }
  await dependencies.retrySleep(delay);
  // Waking up on the deadline itself: fail with the real failure rather than
  // spend the caller's last millisecond on a request that cannot land.
  if (
    remainingOutageSeconds(
      retry.outageStartedAt,
      dependencies.now(),
      params.maxOutageMs,
    ) <= 0
  ) {
    throw error;
  }
}
